import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'

function Card({ title, children }) {
  return (
    <div className="w-full rounded-2xl border border-border-lines p-4">
      <p className="text-xs font-semibold text-secondary tracking-wide uppercase mb-3">{title}</p>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  )
}

function TagList({ value }) {
  const items = (value ?? '').split(',').map((item) => item.trim()).filter(Boolean)
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {items.map((item) => <span key={item} className="bg-background rounded-full px-2.5 py-0.5 text-xs font-medium text-dark-blue">{item}</span>)}
    </div>
  )
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5">
      {Icon && <Icon size={16} className="text-secondary shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-secondary">{label}</p>
        <div className="text-sm text-dark-blue mt-0.5">{children}</div>
      </div>
    </div>
  )
}

function SplitRow({ icon: Icon, leftLabel, leftValue, rightLabel, rightValue }) {
  return (
    <div className="flex items-start gap-2.5">
      {Icon && <Icon size={16} className="text-secondary shrink-0 mt-0.5" />}
      <div className="flex-1 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-secondary">{leftLabel}</p>
          <p className="text-sm text-dark-blue mt-0.5">{leftValue}</p>
        </div>
        <div>
          <p className="text-xs text-secondary">{rightLabel}</p>
          <p className="text-sm text-dark-blue mt-0.5">{rightValue}</p>
        </div>
      </div>
    </div>
  )
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-secondary">{label}</span>
      <span className="text-sm font-medium text-dark-blue">{value}</span>
    </div>
  )
}

export default withTranslation()(function TargetingScheduleTab({ t, campaign }) {
  const f = t('rewardAds.detail.targeting_schedule', { returnObjects: true })
  const { targetSummary, scheduleSummary } = campaign

  const interestCount = (targetSummary.interests ?? '').split(',').filter(Boolean).length
  const locationCount = (targetSummary.location ?? '').split(',').filter(Boolean).length
  const estimatedAudience = 500000 + (interestCount * 220000) + (locationCount * 180000)
  const estimatedDailyImpressions = Math.round(estimatedAudience / 4)

  return (
    <div className="grid md:grid-cols-2 gap-4 items-start">
      <div className="flex flex-col gap-4">
        <Card title={f.targeting_title}>
          <Row icon={UIcons.UilUsersAlt} label={f.interests}><TagList value={targetSummary.interests} /></Row>
          <Row icon={UIcons.UilLocationPinAlt} label={f.location}><TagList value={targetSummary.location} /></Row>
          <Row icon={UIcons.UilShieldCheck} label={f.ip_targeting}><TagList value={targetSummary.ipTargeting} /></Row>
          {
            campaign.realTargeting && (
              <>
                <Row icon={UIcons.UilUser} label={f.gender}>{campaign.realTargeting.gender}</Row>
                <Row icon={UIcons.UilUser} label={f.marital_status}>{campaign.realTargeting.maritalStatus}</Row>
                <Row icon={UIcons.UilCalendarAlt} label={f.age_range}>{campaign.realTargeting.ageRange}</Row>
                {campaign.realTargeting.location && <Row icon={UIcons.UilLocationPinAlt} label={f.geo_location}>{campaign.realTargeting.location}</Row>}
              </>
            )
          }
          <Row icon={UIcons.UilInfoCircle} label={f.audience_summary}>{f.audience_summary_text}</Row>
        </Card>

        <Card title={f.schedule_title}>
          <SplitRow
            icon={UIcons.UilWallet}
            leftLabel={f.budget} leftValue={`NGN ${Number(campaign.budget).toLocaleString()}`}
            rightLabel={f.daily_budget} rightValue={`NGN ${Number(campaign.dailyBudget).toLocaleString()}`}
          />
          <SplitRow
            icon={UIcons.UilCalendarAlt}
            leftLabel={f.start_date} leftValue={scheduleSummary.startDate}
            rightLabel={f.end_date} rightValue={scheduleSummary.endDate}
          />
          <Row icon={UIcons.UilPlayCircle} label={f.days_to_display}><TagList value={scheduleSummary.daysToDisplay} /></Row>
          <Row icon={UIcons.UilClockThree} label={f.hours_to_display}>{scheduleSummary.hoursToDisplay}</Row>
          <Row icon={UIcons.UilSync} label={f.frequency}>{scheduleSummary.frequency}</Row>
        </Card>
      </div>

      <div className="flex flex-col gap-4">
        <Card title={f.campaign_summary_title}>
          <SummaryRow label={f.estimated_audience} value={`${estimatedAudience.toLocaleString()} People`} />
          <SummaryRow label={f.targeted_locations} value={`${locationCount} Cities`} />
          <SummaryRow label={f.interests_selected} value={interestCount} />
          <SummaryRow label={f.ip_ranges} value={targetSummary.ipTargeting} />
          <SummaryRow label={f.estimated_daily_reach} value={campaign.audience.targetingSummary.estimatedReach} />
          <SummaryRow label={f.estimated_daily_impressions} value={estimatedDailyImpressions.toLocaleString()} />
        </Card>

        <Card title={f.scheduled_recap_title}>
          <SummaryRow label={f.campaign_period} value={campaign.period.label} />
          <SummaryRow label={f.days_to_display} value={scheduleSummary.daysToDisplay} />
          <SummaryRow label={f.hours_to_display} value={scheduleSummary.hoursToDisplay} />
          <SummaryRow label={f.frequency} value={scheduleSummary.frequency} />
        </Card>
      </div>
    </div>
  )
})
